import { useState, useCallback, useMemo } from 'react';
import { generateReportPDF, generateReportExcel, createReportConfig } from '@utils/reportHelper';
import { notifyError } from '@config/tostifyConfig/tostifyConfig';

// Default green theme styles - defined outside component to avoid re-creation
const DEFAULT_STYLES = {
    // Header styles
    titleFontSize: '20px',
    titleColor: '#28a745',
    subtitleFontSize: '12px',
    subtitleColor: '#555555',
    
    // Table styles
    tableFontSize: '9px',
    tableHeaderBgColor: '#28a745',
    tableHeaderTextColor: '#ffffff',
    tableCellBorderColor: '#dee2e6',
    tableRowEvenBgColor: '#f1f8f4',
    tableRowOddBgColor: '#ffffff',
    
    // Footer styles
    footerFontSize: '8px',
    footerColor: '#666666',
};

/**
 * Global hook for generating PDF and Excel reports with custom styles
 * @param {Object} config - Report configuration
 * @param {string} config.title - Report title
 * @param {Array} config.columns - Column definitions [{key, label, render?}]
 * @param {string} config.fileName - Export file name
 * @param {string} config.companyName - Company name (optional)
 * @param {Object} config.customStyles - Custom styles (optional)
 * @param {Function} config.reportService - Function to fetch report data
 */
export const useReportGenerator = ({
    title,
    columns,
    fileName,
    companyName = '',
    customStyles = {},
    reportService
}) => {
    const [pdfLoading, setPdfLoading] = useState(false);
    const [excelLoading, setExcelLoading] = useState(false);

    // Merge default styles with custom styles
    const mergedStyles = useMemo(() => ({ ...DEFAULT_STYLES, ...customStyles }), [customStyles]);

    // Memoize report config to update when title/columns change (i.e., when language changes)
    const reportConfig = useMemo(() => createReportConfig(
        title,
        columns,
        fileName,
        companyName,
        mergedStyles
    ), [title, columns, fileName, companyName, mergedStyles]);

    const generatePdfReport = useCallback(async () => {
        if (!reportService) {
            notifyError('Report service not configured');
            return;
        }

        try {
            await generateReportPDF(reportService, reportConfig, setPdfLoading);
        } catch (error) {
            console.error('Error generating PDF report:', error);
            notifyError('Failed to generate PDF report');
        }
    }, [reportService, reportConfig]);

    const generateExcelReport = useCallback(async () => {
        if (!reportService) {
            notifyError('Report service not configured');
            return;
        }

        try {
            await generateReportExcel(reportService, reportConfig, setExcelLoading);
        } catch (error) {
            console.error('Error generating Excel report:', error);
            notifyError('Failed to generate Excel report');
        }
    }, [reportService, reportConfig]);

    return {
        generatePdfReport,
        generateExcelReport,
        pdfLoading,
        excelLoading,
        // Backward compatibility
        generateReport: generatePdfReport,
        loading: pdfLoading
    };
};

